import { useState, useCallback, useRef } from "react";

interface UsePaginationOptions {
  /** Items per page (default: 20) */
  pageSize?: number;
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number;
}

interface UsePaginationReturn<TFilters extends Record<string, unknown>> {
  /** Current page number (1-based) */
  page: number;
  /** Set page directly */
  setPage: (page: number) => void;
  /** Raw (non-debounced) search string for controlled input */
  searchQuery: string;
  /** Debounced search string for use in queries */
  debouncedSearch: string;
  /** Update search with debounce — resets page to 1 */
  setSearch: (value: string) => void;
  /** Current filter values */
  filters: TFilters;
  /** Update a single filter — resets page to 1 */
  setFilter: <K extends keyof TFilters>(key: K, value: TFilters[K]) => void;
  /** Update multiple filters at once — resets page to 1 */
  setFilters: (partial: Partial<TFilters>) => void;
  /** Reset search + all filters + page */
  resetAll: () => void;
  /** Items per page */
  pageSize: number;
}

/**
 * Generic pagination hook that manages page, debounced search, and arbitrary filters.
 *
 * @example
 * ```tsx
 * const { page, setPage, searchQuery, debouncedSearch, setSearch, filters, setFilter } =
 *   usePagination({ pageSize: 20 }, { status: "", paymentStatus: "" });
 * ```
 */
export function usePagination<TFilters extends Record<string, unknown> = Record<string, never>>(
  options?: UsePaginationOptions,
  initialFilters?: TFilters,
): UsePaginationReturn<TFilters> {
  const pageSize = options?.pageSize ?? 20;
  const debounceMs = options?.debounceMs ?? 300;
  const defaultFilters = (initialFilters ?? {}) as TFilters;

  const [page, setPageRaw] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFiltersRaw] = useState<TFilters>(defaultFilters);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setPage = useCallback((p: number) => {
    setPageRaw(p);
  }, []);

  const setSearch = useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setDebouncedSearch(value);
        setPageRaw(1);
      }, debounceMs);
    },
    [debounceMs],
  );

  const setFilter = useCallback(
    <K extends keyof TFilters>(key: K, value: TFilters[K]) => {
      setFiltersRaw((prev) => ({ ...prev, [key]: value }));
      setPageRaw(1);
    },
    [],
  );

  const setFilters = useCallback((partial: Partial<TFilters>) => {
    setFiltersRaw((prev) => ({ ...prev, ...partial }));
    setPageRaw(1);
  }, []);

  const resetAll = useCallback(() => {
    setSearchQuery("");
    setDebouncedSearch("");
    setFiltersRaw(defaultFilters);
    setPageRaw(1);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    page,
    setPage,
    searchQuery,
    debouncedSearch,
    setSearch,
    filters,
    setFilter,
    setFilters,
    resetAll,
    pageSize,
  };
}
